import { useState, useEffect } from "react";
import { API_ENDPOINTS } from "../../config/api";

interface RequestQuotePopupProps {
    isOpen: boolean;
    onClose: () => void;
    /** Pre-filled service name shown inside the modal */
    serviceName?: string;
}

export default function RequestQuotePopup({
    isOpen,
    onClose,
    serviceName,
}: RequestQuotePopupProps) {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        details: "",
        organization: "",
        contactMethod: "email",
        deadline: new Date().toISOString().split('T')[0], // Default to today
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

    // Reset status when popup opens/closes
    useEffect(() => {
        if (!isOpen) {
            setSubmitStatus("idle");
            setFormData(prev => ({ ...prev, name: "", email: "", phone: "", details: "" }));
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus("idle");

        try {
            const formDataToSubmit = new FormData();
            formDataToSubmit.append("name", formData.name);
            formDataToSubmit.append("email", formData.email);
            formDataToSubmit.append("phone", formData.phone);
            formDataToSubmit.append("details", formData.details);
            formDataToSubmit.append("service", serviceName || "General Inquiry");
            formDataToSubmit.append("organization", formData.organization);
            formDataToSubmit.append("contactMethod", formData.contactMethod);
            formDataToSubmit.append("deadline", formData.deadline);

            const response = await fetch(
                API_ENDPOINTS.CONTACT_FORM,
                {
                    method: "POST",
                    body: formDataToSubmit,
                }
            );

            if (response.ok) {
                setSubmitStatus("success");
            } else {
                setSubmitStatus("error");
            }
        } catch (error) {
            console.error("Submission error:", error);
            setSubmitStatus("error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setSubmitStatus("idle");
        onClose();
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={handleClose}
        >
            <div
                className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-[fadeIn_0.2s_ease]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-[#e18033] to-[#c96a1a] px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-3 text-white">
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                            <i className="ri-mail-send-line text-xl" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold">Request a Quote</h2>
                            {serviceName && (
                                <p className="text-sm text-white/80">{serviceName}</p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-white hover:bg-white/30 transition cursor-pointer"
                    >
                        <i className="ri-close-line text-lg" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-6">
                    {submitStatus === "success" ? (
                        /* ── Success State ────────────────────────────────────────── */
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                <i className="ri-check-double-line text-3xl text-green-600 dark:text-green-400" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                                Thank You!
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
                                We've received your request. Our team will reach out to you
                                within 24 hours.
                            </p>
                            <button
                                onClick={handleClose}
                                className="px-6 py-2.5 bg-[#e18033] text-white rounded-xl font-semibold text-sm hover:bg-[#c96a1a] transition cursor-pointer"
                            >
                                Close
                            </button>
                        </div>
                    ) : (
                        /* ── Form ─────────────────────────────────────────────────── */
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                        Full Name *
                                    </label>
                                    <input
                                        required
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="John Doe"
                                        className="w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-sm bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#e18033]/40 focus:border-[#e18033] outline-none transition"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                        Email *
                                    </label>
                                    <input
                                        required
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="john@example.com"
                                        className="w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-sm bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#e18033]/40 focus:border-[#e18033] outline-none transition"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                    Phone Number
                                </label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="+1 (555) 000-0000"
                                    className="w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-sm bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#e18033]/40 focus:border-[#e18033] outline-none transition"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                    Service Interested In
                                </label>
                                <input
                                    type="text"
                                    readOnly
                                    value={serviceName || "General Inquiry"}
                                    className="w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-sm bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-gray-300 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                    Project Details
                                </label>
                                <textarea
                                    rows={3}
                                    name="details"
                                    value={formData.details}
                                    onChange={handleInputChange}
                                    placeholder="Briefly describe your project requirements..."
                                    className="w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl text-sm bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-[#e18033]/40 focus:border-[#e18033] outline-none transition resize-none"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full py-3 bg-[#e18033] hover:bg-[#c96a1a] text-white font-semibold rounded-xl transition-colors text-sm flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <i className={isSubmitting ? "ri-loader-4-line animate-spin" : "ri-send-plane-fill"} />
                                {isSubmitting ? "Sending..." : "Submit Request"}
                            </button>

                            {submitStatus === "error" && (
                                <p className="text-red-600 text-[11px] text-center mt-2">
                                    Something went wrong. Please try again or contact us directly.
                                </p>
                            )}

                            <p className="text-[11px] text-gray-400 dark:text-gray-500 text-center">
                                We respect your privacy and will never share your information.
                            </p>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
